import { Directive, ElementRef, HostListener, Renderer2, AfterViewInit } from '@angular/core';

@Directive({
    selector: '[queHighlightAtWords]',
    standalone: true
})
export class HighlightAtWordsDirective implements AfterViewInit {
    private overlay: HTMLElement | null = null;
    private isInitialized = false;

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    ngAfterViewInit() {
        setTimeout(() => {
            this.setupOverlay();
            this.highlightAtWords();
        });
    }

    @HostListener('input')
    @HostListener('change')
    onInput() {
        this.highlightAtWords();
        this.syncScroll();
    }

    @HostListener('scroll')
    onScroll() {
        this.syncScroll();
    }

    private syncScroll() {
        if (this.overlay) {
            const textarea = this.el.nativeElement as HTMLTextAreaElement;
            this.overlay.scrollTop = textarea.scrollTop;
            this.overlay.scrollLeft = textarea.scrollLeft;
        }
    }

    private setupOverlay() {
        if (this.isInitialized) return;

        const textarea = this.el.nativeElement as HTMLTextAreaElement;
        const parent = textarea.parentElement;

        if (!parent || parent.classList.contains('at-words-container')) {
            return;
        }

        const computedStyle = getComputedStyle(textarea);

        // Create background div
        const background = this.renderer.createElement('div');
        this.renderer.addClass(background, 'textarea-background');
        this.renderer.setStyle(background, 'position', 'absolute');
        this.renderer.setStyle(background, 'top', '0');
        this.renderer.setStyle(background, 'left', '0');
        this.renderer.setStyle(background, 'right', '0');
        this.renderer.setStyle(background, 'bottom', '0');
        this.renderer.setStyle(background, 'background', computedStyle.backgroundColor || 'white');
        this.renderer.setStyle(background, 'border', computedStyle.border);
        this.renderer.setStyle(background, 'border-radius', computedStyle.borderRadius);
        this.renderer.setStyle(background, 'z-index', '0');

        // Create overlay div
        this.overlay = this.renderer.createElement('div');
        this.renderer.addClass(this.overlay, 'at-words-overlay');
        this.renderer.setStyle(this.overlay, 'position', 'absolute');
        this.renderer.setStyle(this.overlay, 'top', '0');
        this.renderer.setStyle(this.overlay, 'left', '0');
        this.renderer.setStyle(this.overlay, 'width', '100%');
        this.renderer.setStyle(this.overlay, 'height', '100%');
        this.renderer.setStyle(this.overlay, 'pointer-events', 'none');
        this.renderer.setStyle(this.overlay, 'overflow', 'hidden');
        this.renderer.setStyle(this.overlay, 'z-index', '1');

        // Copy exact text styles from textarea
        this.renderer.setStyle(this.overlay, 'white-space', 'pre-wrap');
        this.renderer.setStyle(this.overlay, 'word-wrap', 'break-word');
        this.renderer.setStyle(this.overlay, 'padding', computedStyle.padding);
        this.renderer.setStyle(this.overlay, 'margin', '0');
        this.renderer.setStyle(this.overlay, 'border', computedStyle.border);
        this.renderer.setStyle(this.overlay, 'border-color', 'transparent');
        this.renderer.setStyle(this.overlay, 'font-family', computedStyle.fontFamily);
        this.renderer.setStyle(this.overlay, 'font-size', computedStyle.fontSize);
        this.renderer.setStyle(this.overlay, 'font-weight', computedStyle.fontWeight);
        this.renderer.setStyle(this.overlay, 'line-height', '1.8');
        this.renderer.setStyle(this.overlay, 'letter-spacing', computedStyle.letterSpacing);
        this.renderer.setStyle(this.overlay, 'word-spacing', computedStyle.wordSpacing);
        this.renderer.setStyle(this.overlay, 'text-align', computedStyle.textAlign);
        this.renderer.setStyle(this.overlay, 'box-sizing', computedStyle.boxSizing);

        // Make textarea text transparent but keep caret visible
        this.renderer.setStyle(textarea, 'position', 'relative');
        this.renderer.setStyle(textarea, 'background', 'transparent');
        this.renderer.setStyle(textarea, 'line-height', '1.8');
        this.renderer.setStyle(textarea, 'z-index', '2');
        this.renderer.setStyle(textarea, 'color', 'transparent');
        this.renderer.setStyle(textarea, 'caret-color', 'black');
        this.renderer.setStyle(textarea, '-webkit-text-fill-color', 'transparent');
        this.renderer.setStyle(textarea, 'width', 'calc(100% - 22px)');

        // Create container
        const container = this.renderer.createElement('div');
        this.renderer.addClass(container, 'at-words-container');
        this.renderer.setStyle(container, 'position', 'relative');
        this.renderer.setStyle(container, 'display', 'block');
        this.renderer.setStyle(container, 'width', '100%');

        // Insert container and append elements
        this.renderer.insertBefore(parent, container, textarea);
        this.renderer.appendChild(container, background);
        this.renderer.appendChild(container, this.overlay);
        this.renderer.appendChild(container, textarea);

        this.isInitialized = true;
    }

    private highlightAtWords() {
        if (!this.overlay) return;

        const textarea = this.el.nativeElement as HTMLTextAreaElement;
        let text = textarea.value;

        // Replace specific @words with styled spans (gray and italic)
        const atWords = ['@firstName', '@lastName', '@questionnaireLink', '@questionnaireName', '@dueDate'];
        const atPattern = new RegExp(`(${atWords.join('|')})`, 'g');
        text = text.replace(atPattern, '<span style="color: #999; font-style: italic;">$1</span>');

        // Replace specific words with bold (black and bold)
        const boldWords = ['Boardirector'];
        const boldPattern = new RegExp(`(${boldWords.join('|')})`, 'g');
        text = text.replace(boldPattern, '<span style="font-weight: bold;">$1</span>');

        const finalText = text.replace(/^(.*)$/gm, '<span style="color: black;">$1</span>');

        this.overlay.innerHTML = finalText || '<br>';
    }
}
